import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { Column, DataTable } from './DataTable';
import { useSessions } from '../../hooks/useSessions';
import { EnhancedSession } from '../../types/ui/userSession';

import { getUserStatistics } from '../../services/ShootingData/service';
import { UserStatistics } from '../../types/ui/userStatistics';
import { useAtomValue } from 'jotai';
import { userAtom } from '../../state/atoms/authAtoms';
import { toUserStatistics } from '../../adapters/userStatisticsAdapter';

const ShootingData: React.FC = () => {
  const navigate = useNavigate();
  const {
    data: sessions = [],
    isLoading,
    error,
  } = useSessions();

  const currentUser = useAtomValue(userAtom);
  const [stats, setStats] = useState<UserStatistics | null>(null);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        if (!currentUser?.user_id) return;
        const rawStats = await getUserStatistics(currentUser.user_id);
        setStats(toUserStatistics(rawStats));
      } catch (err) {
        console.error('Failed to load user statistics:', err);
        setStats(null);
      }
    };

    fetchStats();
  }, [currentUser?.user_id]);

  const prepareDisplaySessions = (sessions: EnhancedSession[]): EnhancedSession[] => {
    const groupedByDate: Record<string, EnhancedSession[]> = {};

    sessions.forEach(session => {
      if (!groupedByDate[session.dateLabel]) {
        groupedByDate[session.dateLabel] = [];
      }
      groupedByDate[session.dateLabel].push(session);
    });

    return sessions.map(session => {
      const group = groupedByDate[session.dateLabel];
      if (group.length === 1) {
        return { ...session, displayDateLabel: session.dateLabel };
      }
      const index = group.findIndex(s => s.id === session.id);
      const labelWithIndex = `${session.dateLabel} - ${(index + 1).toString().padStart(2, '0')}`;
      return { ...session, displayDateLabel: labelWithIndex };
    });
  };

  if (error) {
    return (
      <Box sx={{ p: 4, backgroundColor: '#0D1117', minHeight: '100vh' }}>
        <Alert severity="error">{error.message}</Alert>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const displaySessions = prepareDisplaySessions(sessions);

  const filteredSessions = displaySessions.filter(session =>
    session.displayDateLabel?.toLowerCase().includes(searchText.toLowerCase())
  );

  const sessionColumns: Column<EnhancedSession>[] = [
    {
      header: 'Date',
      accessor: s => s.displayDateLabel ?? s.dateLabel,
      align: 'center',
    },
    {
      header: 'Time',
      accessor: s => s.timeLabel,
      align: 'center',
    },
    {
      header: 'Duration',
      accessor: s => s.durationLabel,
      align: 'center',
    },
    {
      header: 'Scenarios',
      accessor: s => s.scenarios.toString(),
      align: 'center',
    },
    {
      header: (
        <Box sx={{ ml: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <TextField
            placeholder="Search date..."
            size="small"
            variant="outlined"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'white' }} />
                </InputAdornment>
              ),
              sx: { color: 'white' },
            }}
            sx={{
              backgroundColor: '#0F1A20',
              borderRadius: 1,
              width: 180,
              '& .MuiInputBase-input': { color: 'white' },
            }}
          />
        </Box>
      ),
      align: 'center',
    },
  ];

  return (
    <Box sx={{ p: 4, backgroundColor: 'rgba(14,23,32,0)', minHeight: '100vh' }}>
      <DataTable<EnhancedSession>
        title="Sessions"
        data={filteredSessions}
        isLoading={isLoading}
        error={undefined}
        columns={sessionColumns}
        onRowClick={s =>
          navigate(`${s.id}`, {
            state: {
              session: s,
              stats,
            },
          })
        }
      />
    </Box>
  );
};

export default ShootingData;
